import { createContext, useContext } from 'react';

/** Non-sensitive metadata describing a triggered artifact download. */
export type SidekickArtifactDownloadedEvent =
  | {
      artifactType: 'chart';
      /** Backend-issued position of the requested chart. */
      position: number;
    }
  | {
      artifactType: 'table';
      /** Number of rows present in the exported `dataJson` payload. */
      rowCount: number;
      /** Number of columns present in the exported `dataJson` payload. */
      columnCount: number;
    };

/** Host callback fired after a browser artifact download is triggered. */
export type SidekickArtifactDownloadedCallback = (
  event: SidekickArtifactDownloadedEvent
) => void;

/**
 * Optional callbacks the Sidekick panel forwards to nested package components.
 * Hosts can add analytics or cleanup without reimplementing package behavior.
 */
export type SidekickPanelContextValue = {
  /**
   * Optional consumer hook fired AFTER the package has finished
   * resetting the chat from the in-chat confirmation prompt. Receives
   * no arguments. The chat slice and UI slice are already reset by
   * the time this fires, so consumers observe a clean store.
   */
  onNewConversation?: () => void;
  /**
   * Optional host hook fired after a chart or table browser download is
   * triggered without an immediate error. Signed URLs, filenames, prompts,
   * and table contents are omitted.
   */
  onArtifactDownloaded?: SidekickArtifactDownloadedCallback;
  /**
   * Optional host router hook an action result link calls with its web-app
   * path, so the page changes without a reload. Without it the link loads
   * the page.
   */
  onNavigate?: (path: string) => void;
};

const DEFAULT_VALUE: SidekickPanelContextValue = {};

/**
 * Package-internal context. Exported for the rare consumer that
 * mounts the panel's children manually; production code should rely
 * on `<AISidekickPanel />` to provide the value.
 */
export const SidekickPanelContext =
  createContext<SidekickPanelContextValue>(DEFAULT_VALUE);

/** Convenience hook for components nested under `AISidekickPanel`. */
export function useSidekickPanelContext(): SidekickPanelContextValue {
  return useContext(SidekickPanelContext);
}
